/**
 * @pj/state — the save model: flags, vars, progression, and a versioned,
 * migratable serialized form. Pure and serializable so the same model backs the
 * client (localStorage today) and the server (Cloudflare D1 later).
 *
 * Terminology: "Levels" are the game's stages (Level 1, Level 2 — maps with a
 * gate that opens on completion). The EXP progression is called "Power".
 */

export const SAVE_VERSION = 3;

/** Persistent state of a running Level-2 ("Bhaav") attempt. Lives in the save
 *  so leaving the level, reloading the page, etc. never resets an attempt —
 *  only a timer loss clears it. */
export interface BhaavState {
  /** Wall-clock epoch ms when the attempt's timer expires. */
  deadline: number;
  /** Seed for this attempt's sudoku — regenerated on every new attempt. */
  sudokuSeed: number;
  /** Full 81-char sudoku grid as currently filled in ('0' = empty). */
  entries: string;
  /** The sudoku has been solved (hug unlocked). */
  solved: boolean;
  /** Easter egg: the house closet has been opened. */
  closetOpened: boolean;
  /** Easter egg: AANYA's favourite TOP taken from the closet (gift unlocked). */
  hasTop: boolean;
}

/** Where poopie is standing (restored on load). */
export interface SavedPos {
  mapId: string;
  tx: number;
  ty: number;
  facing: "up" | "down" | "left" | "right";
}

export interface SaveState {
  version: number;
  flags: Record<string, boolean>;
  vars: Record<string, number>;
  exp: number;
  /** EXP progression tier (formerly "level" — renamed so game Levels own the word). */
  power: number;
  /** Unlocked ability ids (e.g. "surf"). */
  abilities: string[];
  /** Key items in the bag (the RUSTY KEY stays with poopie forever). */
  items: string[];
  /** Scripted doors that have been unlocked (stay open). */
  openDoors: string[];
  /** Level id → treasure ids already collected (they don't respawn). */
  treasures: Record<string, string[]>;
  /** Level id → dateKey (YYYY-MM-DD) of the day it was completed. */
  levelsDone: Record<string, string>;
  /** The level the player is currently in (spawn map on load). */
  currentLevel: string;
  /** Exact standing position, restored on load (null = the level's spawn). */
  pos: SavedPos | null;
  /** Running Level-2 attempt, or null when none is active. */
  bhaav: BhaavState | null;
}

export function newSave(): SaveState {
  return {
    version: SAVE_VERSION,
    flags: {},
    vars: {},
    exp: 0,
    power: 1,
    abilities: [],
    items: [],
    openDoors: [],
    treasures: {},
    levelsDone: {},
    currentLevel: "level1",
    pos: null,
    bhaav: null,
  };
}

export function hasItem(save: SaveState, item: string): boolean {
  return save.items.includes(item);
}
export function giveItem(save: SaveState, item: string): boolean {
  if (save.items.includes(item)) return false;
  save.items.push(item);
  return true;
}

export function isTreasureCollected(save: SaveState, levelId: string, treasureId: string): boolean {
  return save.treasures[levelId]?.includes(treasureId) === true;
}
export function markTreasureCollected(save: SaveState, levelId: string, treasureId: string): void {
  const list = save.treasures[levelId] ?? (save.treasures[levelId] = []);
  if (!list.includes(treasureId)) list.push(treasureId);
}

export function getFlag(save: SaveState, flag: string): boolean {
  return save.flags[flag] === true;
}
export function setFlag(save: SaveState, flag: string, value = true): void {
  save.flags[flag] = value;
}
export function getVar(save: SaveState, name: string): number {
  return save.vars[name] ?? 0;
}
export function setVar(save: SaveState, name: string, value: number): void {
  save.vars[name] = value;
}

export function hasAbility(save: SaveState, ability: string): boolean {
  return save.abilities.includes(ability);
}
export function grantAbility(save: SaveState, ability: string): boolean {
  if (save.abilities.includes(ability)) return false;
  save.abilities.push(ability);
  return true;
}

// --- progression (Power) -----------------------------------------------------

/** Cumulative EXP required to *reach* a given Power (triangular curve). */
export function expToReach(power: number): number {
  if (power <= 1) return 0;
  return (100 * (power - 1) * power) / 2;
}

export function powerForExp(exp: number): number {
  let power = 1;
  while (expToReach(power + 1) <= exp) power++;
  return power;
}

/** EXP threshold that begins the next Power tier (for HUD bars). */
export function expForNext(save: SaveState): number {
  return expToReach(save.power + 1);
}

/** Add EXP, recompute Power. Returns the new Power if one was gained, else null. */
export function addExp(save: SaveState, amount: number): number | null {
  save.exp += amount;
  const newPower = powerForExp(save.exp);
  if (newPower > save.power) {
    save.power = newPower;
    return newPower;
  }
  save.power = newPower;
  return null;
}

// --- level completion ---------------------------------------------------------

export function isLevelDone(save: SaveState, levelId: string): boolean {
  return typeof save.levelsDone[levelId] === "string";
}
export function markLevelDone(save: SaveState, levelId: string, dateKey: string): void {
  if (!isLevelDone(save, levelId)) save.levelsDone[levelId] = dateKey;
}

// --- serialization + migration ---------------------------------------------

export function serialize(save: SaveState): string {
  return JSON.stringify(save);
}

/** Parse + migrate an unknown blob to the current SaveState shape. Tolerant:
 *  unknown/old blobs fall back to a fresh save rather than throwing. */
export function deserialize(blob: string | null | undefined): SaveState {
  if (!blob) return newSave();
  let raw: unknown;
  try {
    raw = JSON.parse(blob);
  } catch {
    return newSave();
  }
  if (typeof raw !== "object" || raw === null) return newSave();
  const base = newSave();
  const obj = raw as Partial<SaveState> & LegacyFields;
  const merged: SaveState & LegacyFields = {
    ...base,
    ...obj,
    flags: { ...base.flags, ...(obj.flags ?? {}) },
    vars: { ...base.vars, ...(obj.vars ?? {}) },
    abilities: obj.abilities ?? base.abilities,
    items: obj.items ?? base.items,
    openDoors: obj.openDoors ?? base.openDoors,
    treasures: obj.treasures ?? base.treasures,
    levelsDone: obj.levelsDone ?? base.levelsDone,
    pos: obj.pos ?? base.pos,
    bhaav: obj.bhaav ?? base.bhaav,
  };
  return migrate(merged);
}

/** Pre-v3 fields that migrations read and then drop. */
interface LegacyFields {
  /** v1/v2: EXP progression tier (renamed to `power` in v3). */
  level?: number;
  /** v1/v2: dateKey → daily-hunt ids completed that day (retired in v3). */
  completed?: Record<string, string[]>;
}

/** Forward-migrate older save versions. New versions append a case here. */
function migrate(save: SaveState & LegacyFields): SaveState {
  if (save.version < 2) {
    // v1 → v2: Fly was replaced by the Surf quest (key → house → SURF CHARM).
    save.abilities = save.abilities.filter((a) => a !== "fly");
  }
  if (save.version < 3) {
    // v2 → v3: "level" (EXP tier) became "power"; daily treasure hunts became
    // game Levels — any completed hunt means Level 1 is done (its gate is open).
    if (typeof save.level === "number" && save.level > 0) save.power = save.level;
    const huntDays = Object.keys(save.completed ?? {})
      .filter((day) => (save.completed?.[day]?.length ?? 0) > 0)
      .sort();
    const lastDay = huntDays[huntDays.length - 1];
    if (lastDay) save.levelsDone["level1"] = lastDay;
    save.currentLevel = save.currentLevel || "level1";
    save.bhaav = save.bhaav ?? null;
  }
  delete save.level;
  delete save.completed;
  save.version = SAVE_VERSION;
  return save;
}
